import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { suppliersAPI } from '@/lib/api-client'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SupplierFormDialog } from './suppliers/SupplierFormDialog'
import {
  Plus,
  Search,
  Truck,
  Star,
  ShoppingCart,
  Package,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Loader2,
  UserX,
  LayoutGrid,
  List,
} from 'lucide-react'

const TYPE_LABELS: Record<string, string> = {
  MATERIALS: 'Materiais',
  SERVICES: 'Serviços',
  BOTH: 'Ambos',
}

const TYPE_COLORS: Record<string, string> = {
  MATERIALS: 'bg-blue-100 text-blue-700',
  SERVICES: 'bg-purple-100 text-purple-700',
  BOTH: 'bg-teal-100 text-teal-700',
}

function renderStars(rating: number) {
  const stars = []
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`h-3.5 w-3.5 ${
          i <= rating ? 'fill-amber-400 text-amber-400' : 'text-neutral-300'
        }`}
      />
    )
  }
  return stars
}

function InlineRating({ supplier }: { supplier: any }) {
  const queryClient = useQueryClient()
  const [hovered, setHovered] = useState<number | null>(null)

  const ratingMutation = useMutation({
    mutationFn: (rating: number) => suppliersAPI.update(supplier.id, { rating }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['suppliers'] }),
  })

  const displayed = hovered ?? supplier.rating ?? 0

  return (
    <div className="flex items-center justify-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 cursor-pointer transition-colors ${
            i <= displayed ? 'fill-amber-400 text-amber-400' : 'text-neutral-300'
          }`}
          onMouseEnter={() => setHovered(i)}
          onMouseLeave={() => setHovered(null)}
          onClick={() => ratingMutation.mutate(i)}
        />
      ))}
    </div>
  )
}

export function Suppliers() {
  const navigate = useNavigate()
  const canCreate = usePermission('suppliers:create')

  const [viewMode, setViewMode] = useState<'card' | 'table'>(() =>
    (localStorage.getItem('viewMode:suppliers') as 'card' | 'table') ?? 'card'
  )
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const limit = viewMode === 'table' ? 15 : 9

  const { data, isLoading } = useQuery({
    queryKey: ['suppliers', { page, search, type: typeFilter, isActive: statusFilter, limit }],
    queryFn: () =>
      suppliersAPI.list({
        page,
        limit,
        search: search || undefined,
        type: typeFilter || undefined,
        isActive: statusFilter === '' ? undefined : statusFilter === 'true',
      }),
  })

  const rawSuppliers = data?.data || []
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 }

  const suppliers = sortKey
    ? [...rawSuppliers].sort((a: any, b: any) => {
        let av: any, bv: any
        if (sortKey === 'name') { av = a.name?.toLowerCase(); bv = b.name?.toLowerCase() }
        else if (sortKey === 'document') { av = a.document; bv = b.document }
        else if (sortKey === 'type') { av = a.type; bv = b.type }
        else if (sortKey === 'rating') { av = a.rating || 0; bv = b.rating || 0 }
        else if (sortKey === 'purchaseOrders') { av = a._count?.purchaseOrders || 0; bv = b._count?.purchaseOrders || 0 }
        else if (sortKey === 'materials') { av = a._count?.materials || 0; bv = b._count?.materials || 0 }
        else if (sortKey === 'isActive') { av = a.isActive ? 1 : 0; bv = b.isActive ? 1 : 0 }
        if (av < bv) return sortDir === 'asc' ? -1 : 1
        if (av > bv) return sortDir === 'asc' ? 1 : -1
        return 0
      })
    : rawSuppliers

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (sortKey !== col) return <ChevronsUpDown className="ml-1 h-3.5 w-3.5 text-neutral-400 inline" />
    return sortDir === 'asc'
      ? <ChevronUp className="ml-1 h-3.5 w-3.5 inline" />
      : <ChevronDown className="ml-1 h-3.5 w-3.5 inline" />
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const handleViewMode = (v: 'card' | 'table') => {
    localStorage.setItem('viewMode:suppliers', v)
    setViewMode(v)
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Fornecedores</h1>
          <p className="mt-1 text-neutral-600">Gerencie os fornecedores cadastrados</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center rounded-lg border p-0.5">
            <Button
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleViewMode('card')}
            >
              <LayoutGrid className="mr-1 h-3.5 w-3.5" />Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleViewMode('table')}
            >
              <List className="mr-1 h-3.5 w-3.5" />Tabela
            </Button>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Fornecedor
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <form onSubmit={handleSearch} className="flex flex-1 gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar por nome ou CNPJ..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button type="submit" variant="secondary">Buscar</Button>
        </form>

        <Select
          value={typeFilter || 'ALL'}
          onValueChange={(value) => { setTypeFilter(value === 'ALL' ? '' : value); setPage(1) }}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filtrar por tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os tipos</SelectItem>
            <SelectItem value="MATERIALS">Materiais</SelectItem>
            <SelectItem value="SERVICES">Serviços</SelectItem>
            <SelectItem value="BOTH">Ambos</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={statusFilter || 'ALL'}
          onValueChange={(value) => { setStatusFilter(value === 'ALL' ? '' : value); setPage(1) }}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            <SelectItem value="true">Ativo</SelectItem>
            <SelectItem value="false">Inativo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : suppliers.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-16">
          <UserX className="h-16 w-16 text-neutral-300" />
          <h3 className="mt-4 text-xl font-medium text-neutral-900">
            {search || typeFilter || statusFilter ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor cadastrado'}
          </h3>
          <p className="mt-2 text-neutral-500">
            {search || typeFilter || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Comece cadastrando seu primeiro fornecedor.'}
          </p>
          {canCreate && !search && !typeFilter && !statusFilter && (
            <Button className="mt-6" onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />Cadastrar Primeiro Fornecedor
            </Button>
          )}
        </div>
      ) : viewMode === 'card' ? (
        <>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {suppliers.map((supplier: any) => (
              <Card
                key={supplier.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => navigate(`/suppliers/${supplier.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Truck className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base line-clamp-1">{supplier.name}</CardTitle>
                    </div>
                    <Badge variant={supplier.isActive ? 'default' : 'secondary'}>
                      {supplier.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-neutral-500">{supplier.document}</p>
                  <Badge variant="outline" className={TYPE_COLORS[supplier.type]}>
                    {TYPE_LABELS[supplier.type]}
                  </Badge>
                  <div className="flex items-center gap-1">
                    {renderStars(supplier.rating || 0)}
                    <span className="ml-1 text-sm text-neutral-500">({supplier.rating || 0}/5)</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-neutral-500">
                    <div className="flex items-center gap-1">
                      <ShoppingCart className="h-3.5 w-3.5" />
                      <span>{supplier._count?.purchaseOrders || 0} pedidos</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Package className="h-3.5 w-3.5" />
                      <span>{supplier._count?.materials || 0} materiais</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-neutral-500">
                Mostrando {suppliers.length} de {pagination.total} fornecedores
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />Anterior
                </Button>
                <span className="text-sm text-neutral-600">{page} / {pagination.totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                  Próximo<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="rounded-lg border bg-white overflow-x-auto">
            <div className="min-w-[640px]">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  {[
                    { key: 'name', label: 'Nome', className: 'font-semibold cursor-pointer select-none' },
                    { key: 'document', label: 'CNPJ', className: 'font-semibold cursor-pointer select-none' },
                    { key: 'type', label: 'Tipo', className: 'font-semibold cursor-pointer select-none' },
                    { key: 'rating', label: 'Avaliação', className: 'font-semibold cursor-pointer select-none text-center' },
                    { key: 'purchaseOrders', label: 'Pedidos', className: 'font-semibold cursor-pointer select-none text-center' },
                    { key: 'materials', label: 'Materiais', className: 'font-semibold cursor-pointer select-none text-center' },
                    { key: 'isActive', label: 'Status', className: 'font-semibold cursor-pointer select-none text-center' },
                  ].map(({ key, label, className }) => (
                    <TableHead key={key} className={className} onClick={() => handleSort(key)}>
                      {label}<SortIcon col={key} />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((supplier: any) => (
                  <TableRow
                    key={supplier.id}
                    className="cursor-pointer hover:bg-neutral-50"
                    onClick={() => navigate(`/suppliers/${supplier.id}`)}
                  >
                    <TableCell className="font-medium">{supplier.name}</TableCell>
                    <TableCell className="text-neutral-500 text-sm">{supplier.document || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={TYPE_COLORS[supplier.type]}>
                        {TYPE_LABELS[supplier.type]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <InlineRating supplier={supplier} />
                    </TableCell>
                    <TableCell className="text-center text-sm text-neutral-600">
                      {supplier._count?.purchaseOrders || 0}
                    </TableCell>
                    <TableCell className="text-center text-sm text-neutral-600">
                      {supplier._count?.materials || 0}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={supplier.isActive ? 'default' : 'secondary'}>
                        {supplier.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-neutral-500">
                Mostrando {suppliers.length} de {pagination.total} fornecedores
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />Anterior
                </Button>
                <span className="text-sm text-neutral-600">{page} / {pagination.totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                  Próximo<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <SupplierFormDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
    </div>
  )
}
